import { OktaAuth, Token } from "@okta/okta-auth-js";
import { APP_ENV } from '../../constants';

const CROSS_TAB_RENEWAL_LOCK = 'okta-token-renewal-lock';
const TAB_SYNC_TIMEOUT_MS = 15000;
const MAX_REFRESH_RETRIES = 2;                 // 3 total refresh-token attempts
const REFRESH_RETRY_BACKOFF_MS = [3000, 5000]; // delay before retry 1 and retry 2

const isDefinitiveRefreshFailure = (error: unknown): boolean => {
  if ((error as { definitive?: boolean })?.definitive === true) return true; // explicitly marked
  const name = (error as { name?: string })?.name;
  if (name === 'OAuthError') return true; // token endpoint rejected the grant
  const status = (error as { xhr?: { status?: number } })?.xhr?.status;
  if (name === 'AuthApiError' && typeof status === 'number' && status >= 400 && status < 500) {
    // 408 (Request Timeout) and 429 (Too Many Requests) are transient - keep them retryable.
    return status !== 408 && status !== 429;
  }
  return false;
};


export const getOktaAuth = () => {
  const config = {
    redirectUri: `${window.location.origin}/login/callback`,
    tokenManager: {
      autoRenew: true,
      syncStorage: true,
      expireEarlySeconds: 60 + Math.floor(Math.random() * 60),
      storage: localStorage
    },
    scopes: ['openid', 'profile', 'email', 'offline_access']
  };

  let oktaAuthInstance: OktaAuth;

  if (APP_ENV === 'us-dit') {
    oktaAuthInstance = new OktaAuth({
      ...config,
      issuer: "https://connectcdk-dit.oktapreview.com/oauth2/default",
      clientId: "0oa18b1zpbtWJde4q0h8",
      authorizeUrl: "https://login-dit.connectcdk.com/login/authorize",
      tokenUrl: "https://cml-api-nginx.global-dit.connectcdk.com/token",
    });
  }
  else if (APP_ENV === 'us-stage') {
    oktaAuthInstance = new OktaAuth({
      ...config,
      issuer: "https://connectcdk-dev.oktapreview.com/oauth2/default",
      clientId: "0oa1cmt27fan8DvAh0h8",
      authorizeUrl: "https://login-perf.connectcdk.com/login/authorize",
      tokenUrl: "https://nginx.nebula-perf.connectcdk.com/api/cs-common-login-api/v1/token",
    });
  }
  else if (APP_ENV === 'us-perf') {
    oktaAuthInstance = new OktaAuth({
      ...config,
      issuer: "https://connectcdk-dit.oktapreview.com/oauth2/default",
      clientId: "0oa18b1zpbtWJde4q0h8",
      authorizeUrl: "https://login-dit.connectcdk.com/login/authorize",
      tokenUrl: "https://cml-api-nginx.global-dit.connectcdk.com/token",
    });
  }
  else if (APP_ENV === 'us-prod') {
    oktaAuthInstance = new OktaAuth({
      ...config,
      issuer: "https://connectcdk.okta.com/oauth2/default",
      clientId: "0oaphqy0vfFEv2fzh1t7",
      authorizeUrl: "https://login.connectcdk.com/login/authorize",
      tokenUrl: "https://api.connectcdk.com/api/cs-common-login-api/v1/token",
    });
  } else {
    oktaAuthInstance = new OktaAuth({
      ...config,
      issuer: "https://connectcdk-dit.oktapreview.com/oauth2/default",
      clientId: "0oa18b1zpbtWJde4q0h8",
      authorizeUrl: "https://login-dit.connectcdk.com/login/authorize",
      tokenUrl: "https://cml-api-nginx.global-dit.connectcdk.com/token",
    });
  }

  const originalRenew = oktaAuthInstance.tokenManager.renew.bind(oktaAuthInstance.tokenManager);

  oktaAuthInstance.tokenManager.renew = async function(key: string): Promise<Token> {
    let now = Date.now();
    const lock = localStorage.getItem(CROSS_TAB_RENEWAL_LOCK);
    const lockTime = lock ? parseInt(lock, 10) : null;

    if (lockTime && !isNaN(lockTime) && (now - lockTime) < TAB_SYNC_TIMEOUT_MS) {
      console.log('Another tab is renewing tokens, waiting for it to finish.');

      try {
        const tokens = await this.getTokens();
        const token = tokens[key as keyof typeof tokens];
        if (token && !this.hasExpired(token)) {
          console.log('Token synced from another tab renewal.');
          return token;
        }
      } catch (error) {
        console.error('Error retrieving synced tokens:', error);
      }

      for (let i = 0; i < 3; i++) {
        await new Promise(r => setTimeout(r, 5000));

        now = Date.now();
        const currentLock = localStorage.getItem(CROSS_TAB_RENEWAL_LOCK);
        const currentLockTime = currentLock ? parseInt(currentLock, 10) : null;

        if (!currentLockTime || isNaN(currentLockTime) || (now - currentLockTime) >= TAB_SYNC_TIMEOUT_MS) {
          console.log('Lock released or expired, another tab may have finished or crashed');
          break;
        }

        try {
          const tokens = await this.getTokens();
          const token = tokens[key as keyof typeof tokens];
          if (token && !this.hasExpired(token)) {
            console.log('Token synced from another tab renewal.');
            return token;
          }
        } catch (error) {
          console.error('Error retrieving synced tokens:', error);
        }
      }

      try {
        const tokens = await this.getTokens();
        const token = tokens[key as keyof typeof tokens];
        if (token && !this.hasExpired(token)) {
          console.log('Token synced from another tab renewal after lock released.');
          return token;
        }
      } catch (error) {
        console.error('Error retrieving synced tokens:', error);
      }
    }

    console.log('This tab will renew tokens.');
    localStorage.setItem(CROSS_TAB_RENEWAL_LOCK, now.toString());

    try {
      const refreshToken = await this.get('refreshToken');

      if (!refreshToken) {
        console.log('No refresh token available, using original renew method');
        return await originalRenew(key);
      }

      // Attempt renewal with the refresh token, retrying only on transient failures.
      let definitiveFailure = false;
      for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
        try {
          console.log(`Renewing tokens using refresh token (attempt ${attempt + 1})`);
          const freshTokens = await oktaAuthInstance.token.renewTokens();

          // Validate BEFORE persisting: if renewTokens resolved without the requested
          // token, do not write this incomplete set to storage. Retrying will not help
          // (a successful renewal that omits the key won't self-fix), so mark it
          // definitive to fall back immediately.
          const token = freshTokens[key as keyof typeof freshTokens];
          if (!token) {
            throw Object.assign(new Error(`Token ${key} not found after renewal`), { definitive: true });
          }
          await this.setTokens(freshTokens);
          return token;
        } catch (error) {
          definitiveFailure = isDefinitiveRefreshFailure(error);
          const outOfRetries = attempt === MAX_REFRESH_RETRIES;
          if (definitiveFailure || outOfRetries) {
            console.warn('Refresh-token renewal failed; using traditional renewal.', { definitive: definitiveFailure, attempt, error });
            break;
          }
          await new Promise(r => setTimeout(r, REFRESH_RETRY_BACKOFF_MS[attempt]));
          localStorage.setItem(CROSS_TAB_RENEWAL_LOCK, Date.now().toString()); // keep cross-tab lock fresh
        }
      }

      if (definitiveFailure) {
        console.log('Refresh token is unusable; removing it and renewing via SSO cookie');
        this.remove('refreshToken');
      } else {
        console.log('Falling back to original renew method (refresh token retained)');
      }
      localStorage.setItem(CROSS_TAB_RENEWAL_LOCK, Date.now().toString()); // fallback renewal can be slow; re-stamp lock
      return await originalRenew(key);
    } catch (error) {
      console.error('Token renewal failed:', error);
      throw error;
    } finally {
      localStorage.removeItem(CROSS_TAB_RENEWAL_LOCK);
    }
  };

  return oktaAuthInstance;
}

export const oktaAuth = getOktaAuth() as any;

export const getAccessToken = async () => {
  try {
      return await oktaAuth.getAccessToken();
  } catch (error) {
      console.error("Error fetching access token:", error);
      return null;
  }
};

export const isTokenExpired = () => {
  const expiryTime = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')
    .accessToken
    ?.claims
    ?.exp;
  const expiryTimeMilliseconds = expiryTime * 1000;
  const expiryDate = new Date(expiryTimeMilliseconds);
  const currentDate = new Date();
  return currentDate > expiryDate;
};

export const getExpiryDate =  () => {
  const expiryTime = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')
    .accessToken
    ?.claims
    ?.exp;
  const expiryTimeMilliseconds = expiryTime * 1000;
  return new Date(expiryTimeMilliseconds);
};

export const isAuthenticated = async () => {
  try {
      return await oktaAuth.isAuthenticated(); 
  } catch (error) {
      console.error("Error while authentication:", error);
      return null;
  }
};
